import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {GraphicConfiguration} from "./graphic-configuration";
import {PointConfiguration} from "./geometry-configuration";
import {WidgetConfiguration} from "./widget-configuration";
import {
    ContentConfiguration,
    AttachmentsContentConfiguration,
    FieldsContentConfiguration,
    MediaContentConfiguration,
    TextContentConfiguration,
    CustomContentConfiguration, ExpressionContentConfiguration, RelationshipContentConfiguration
} from "./content-configuration";
import {ActionButtonConfiguration, ActionSliderConfiguration, ActionToggleConfiguration} from "./action-configuration";
import {TextSymbolConfiguration, PictureMarkerSymbolConfiguration} from "./symbol-configuration";
import {FeatureLayerConfiguration} from "./layer-configuration";
import {FillSymbolConfiguration} from "./symbol-configuration";
import Collection from "@arcgis/core/core/Collection";


// A predicate function use to determine what is the symbol type
const contentTypePredicate = (content: any) => {
    if (content.type === "text") {
        return TextContentConfiguration;
    }
    if (content.type === "fields") {
        return FieldsContentConfiguration;
    }
    if (content.type === "media") {
        return MediaContentConfiguration;
    }
    if (content.type === "attachments") {
        return AttachmentsContentConfiguration;
    }
    if (content.type === "custom") {
        return CustomContentConfiguration;
    }
    if (content.type === "expression") {
        return ExpressionContentConfiguration;
    }
    if (content.type === "relationship") {
        return RelationshipContentConfiguration;
    }

    return TextContentConfiguration;
};

// A predicate function use to determine what is the symbol type
const actionTypePredicate = (action: any) => {
    if (action.type === "button") {
        return ActionButtonConfiguration;
    }
    if (action.type === "toggle") {
        return ActionToggleConfiguration;
    }
    if (action.type === "slider") {
        return ActionSliderConfiguration;
    }
    return ActionButtonConfiguration;
};

@JsonObject()
export class PopupDockOptionsBreakpointConfiguration {
    /**
     * The maximum width of the [View](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html) at which the popup will be set to dockEnabled automatically.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#dockOptions)
     */
    @JsonProperty()
    declare width?: number;
    /**
     * The maximum height of the [View](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html) at which the popup will be set to dockEnabled automatically.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#dockOptions)
     */
    @JsonProperty()
    declare height?: number;

    constructor() {}
}

@JsonObject()
export class PopupDockOptionsConfiguration {
    /**
     * Defines the dimensions of the [View](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html) at which to dock the popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#dockOptions)
     */
    @JsonProperty()
    declare breakpoint?: boolean | PopupDockOptionsBreakpointConfiguration;
    /**
     * If `true`, displays the dock button.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#dockOptions)
     */
    @JsonProperty()
    declare buttonEnabled?: boolean;
    /**
     * The position in the view at which to dock the popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#dockOptions)
     */
    @JsonProperty()
    declare position?: string | Function;

    constructor() {}
}
@JsonObject()
export class PopupVisibleElementsConfiguration {
    /**
     * Indicates whether to the feature navigation will be displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#VisibleElements)
     */
    @JsonProperty()
    declare featureNavigation: boolean;
    /**
     * Indicates whether to display a close button on the popup dialog.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#VisibleElements)
     */
    @JsonProperty()
    declare closeButton: boolean;

    /**
     * *Geo.js Property*
     * Display coordinates at the bottom
     */
    @JsonProperty()
    coordinates = true;

    constructor() {}
}
@JsonObject()
export class LayerTemplate {
    // *Geo.js Properties*
    declare metauid?: string;
    declare layerIds?: number[];
    declare mode: "append" | "prepend" | "replace";
    declare template: PopupTemplateConfiguration;
}
@JsonObject()
export class PopupConfiguration extends WidgetConfiguration {
    /**
     * Defines actions that may be executed by clicking the icon or image symbolizing them in the popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#actions)
     */
    @JsonProperty({type: actionTypePredicate})
    declare actions?: Collection<
        (ActionButtonConfiguration & { type: "button" }) | (ActionToggleConfiguration & { type: "toggle" })
    >;

    /**
     * Position of the popup in relation to the selected feature.
     *
     * @default auto
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#alignment)
     */
    @JsonProperty()
    declare alignment: "auto" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right" | Function;
    /**
     * This closes the popup when the [View](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html) camera or [Viewpoint](https://developers.arcgis.com/javascript/latest/api-reference/esri-Viewpoint.html) changes.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#autoCloseEnabled)
     */
    @JsonProperty()
    declare autoCloseEnabled: boolean;
    /**
     * This property indicates to the `Popup` that it needs to allow or disallow the click event propagation.
     *
     * @default true
     * @deprecated since 4.27. Use {@link module:esri/core/MapView#popupEnabled MapView/SceneView.popupEnabled} instead.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#autoOpenEnabled)
     */
    @JsonProperty()
    declare autoOpenEnabled: boolean;
    /**
     * Indicates whether to enable collapse functionality for the popup.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#collapseEnabled)
     */
    @JsonProperty()
    declare collapseEnabled: boolean;
    /**
     * The content of the popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#content)
     */
    @JsonProperty()
    declare content: string | HTMLElement | WidgetConfiguration;

    /**
     * Enables automatic creation of a popup template for layers that have popups enabled but no popupTemplate defined.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#defaultPopupTemplateEnabled)
     */
    @JsonProperty()
    declare defaultPopupTemplateEnabled: boolean;
    /**
     * Indicates whether the placement of the popup is docked to the side of the view.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#dockEnabled)
     */
    @JsonProperty()
    declare dockEnabled: boolean;
    /**
     * Docking the popup allows for a better user experience, particularly when opening popups in apps on mobile devices.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#dockOptions)
     */
    @JsonProperty()
    declare dockOptions: PopupDockOptionsConfiguration;
    /**
     * An array of features associated with the popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#features)
     */
    @JsonProperty()
    declare features: GraphicConfiguration[];
    /**
     * Indicates the heading level to use for the [title](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#title) of the popup.
     *
     * @default 2
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#headingLevel)
     */
    @JsonProperty()
    declare headingLevel?: number;
    /**
     * Highlight the selected popup feature using the [highlightOptions](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#highlightOptions) set on the [MapView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html) or the [highlightOptions](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html#highlightOptions) set on the [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html).
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#highlightEnabled)
     */
    @JsonProperty()
    declare highlightEnabled: boolean;
    /**
     * Point used to position the popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#location)
     */
    @JsonProperty()
    declare location: PointConfiguration;

    /**
     * Defines the maximum icons displayed at one time in the action area.
     *
     * @default 3
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#maxInlineActions)
     */
    @JsonProperty()
    declare maxInlineActions?: number | null;

    /**
     * An array of pending Promises that have not yet been fulfilled.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#promises)
     */
    @JsonProperty()
    declare promises: Array<Promise<any>>;

    /**
     * Index of the feature that is [selected](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#selectedFeature).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#selectedFeatureIndex)
     */
    @JsonProperty()
    declare selectedFeatureIndex: number;
    /**
     * Indicates whether to display a spinner at the popup location prior to its display when it has pending promises.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#spinnerEnabled)
     */
    @JsonProperty()
    declare spinnerEnabled: boolean;
    /**
     * The title of the popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#title)
     */
    @JsonProperty()
    declare title: string;
    /**
     * A reference to the [MapView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html) or [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#view)
     */
    // @JsonProperty()
    // declare view?: (MapViewConfiguration & { type: "2d" }) | (SceneViewConfiguration & { type: "3d" });
    /**
     * This is a class that contains all the logic (properties and methods) that controls this widget's behavior.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#viewModel)
     */
    // @JsonProperty()
    // declare viewModel: PopupViewModelConfiguration;
    /**
     * The visible elements that are displayed within the widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#visibleElements)
     */
    @JsonProperty()
    declare visibleElements: PopupVisibleElementsConfiguration;
    /**
     * *Geo.js Property*
     * Disable Popup.
     */
    @JsonProperty()
    declare disabled?: boolean;
    /**
     * *Geo.js Property*
     * Show data exclusive for one feature.
     */
    @JsonProperty()
    declare exclusiveFeature?: boolean;
    /**
     * *Geo.js Property*
     * Show data exclusive for one feature.
     */
    @JsonProperty()
    declare highlightSymbol?: FillSymbolConfiguration;
    /**
     * *Geo.js Properties*
     * Popup query feature layer
     * avls: AV Liegenschaften
     */
    @JsonProperty()
    declare layers?: HashMap<FeatureLayerConfiguration>;
    /**
     * *Geo.js Properties*
     * MapViewer specific template configuration per layer
     */
    @JsonProperty()
    declare layerTemplates?: LayerTemplate[];
    /**
     * *Geo.js Property*
     * Show data exclusive for one feature.
     */
    @JsonProperty()
    declare pointerSymbol: PictureMarkerSymbolConfiguration | TextSymbolConfiguration;

    constructor() {
        super();
    }
}

@JsonObject()
export class PopupExpressionInfoConfiguration {
    /**
     * An [Arcade](https://developers.arcgis.com/javascript/latest/arcade/) expression following the specification defined by the [Arcade Popup Profile](https://developers.arcgis.com/javascript/latest/arcade/#popup).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-ExpressionInfo.html#expression)
     */
    @JsonProperty()
    declare expression: string;
    /**
     * The name of the expression.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-ExpressionInfo.html#name)
     */
    @JsonProperty()
    declare name: string;
    /**
     * Indicates the return type of the Arcade expression.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-ExpressionInfo.html#returnType)
     */
    @JsonProperty()
    declare returnType?: "string" | "number";
    /**
     * The title used to describe the value returned by the expression in the popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-ExpressionInfo.html#title)
     */
    @JsonProperty()
    declare title: string;

    constructor() {}
}

@JsonObject()
export class PopupTemplateConfiguration {
    /**
     * A [Collection](https://developers.arcgis.com/javascript/latest/api-reference/esri-core-Collection.html) of [action](https://developers.arcgis.com/javascript/latest/api-reference/esri-support-actions-ActionButton.html) or [action toggle](https://developers.arcgis.com/javascript/latest/api-reference/esri-support-actions-ActionToggle.html) objects.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#actions)
     */
    @JsonProperty({type: actionTypePredicate})
    declare actions?: Collection<
        (ActionButtonConfiguration & { type: "button" }) | (ActionToggleConfiguration & { type: "toggle" })
    >;
    /**
     * The template for defining and formatting a popup's content.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#content)
     */
    @JsonProperty({ type: contentTypePredicate })
    declare content: ContentConfiguration[] | string | Function | Promise<any>;
    /**
     * An array of objects or [ExpressionInfo[]](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-ExpressionInfo.html) that reference [Arcade](https://developers.arcgis.com/javascript/latest/arcade/) expressions following the specification defined by the [Arcade Popup Profile](https://developers.arcgis.com/javascript/latest/arcade/#popup).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#expressionInfos)
     */
    @JsonProperty()
    declare expressionInfos: PopupExpressionInfoConfiguration[];
    /**
     * An array of [FieldInfo](https://developers.arcgis.com/javascript/latest/api-reference/esri-popup-FieldInfo.html) that defines how fields in the dataset or values from [Arcade](https://developers.arcgis.com/javascript/latest/arcade/) expressions participate in a popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#fieldInfos)
     */
    // @JsonProperty()
    // declare fieldInfos: FieldInfoConfiguration[];
    /**
     * Indicates whether or not editor tracking should display.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#lastEditInfoEnabled)
     */
    @JsonProperty()
    declare lastEditInfoEnabled: boolean;
    /**
     * Additional options that can be defined for the popup layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#layerOptions)
     */
    // @JsonProperty()
    // declare layerOptions: LayerOptionsConfiguration;
    /**
     * An array of field names used in the PopupTemplate.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#outFields)
     */
    @JsonProperty()
    declare outFields: string[];
    /**
     * Indicates whether actions should replace existing [popup actions](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html#actions).
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#overwriteActions)
     */
    @JsonProperty()
    declare overwriteActions: boolean;
    /**
     * Indicates whether to include the feature's geometry for use by the template.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#returnGeometry)
     */
    @JsonProperty()
    declare returnGeometry?: boolean;
    /**
     * The template for defining how to format the title used in a popup.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html#title)
     */
    @JsonProperty()
    declare title: string | Function | Promise<any>;

    constructor() {}
}
